import React from "react";
import { ConnXClient } from "../../../api/apolloClient";
import moment from "moment";
import { Card } from "react-bootstrap";
import Chip from "@material-ui/core/Chip";
import {
  Tooltip,
  makeStyles,
  Theme,
  createStyles,
  Box,
  LinearProgress,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { withApolloProvider } from "../../../helpers/apollo";
import { ActionMenu, OnDCXDashEvent } from "./live/ActionMenu";
import { useUser } from "../../../userContext";
import {
  Alert,
  LiveData,
  LiveParamNamed,
  lastUpdatedAt,
  OnlineStatus,
} from "./useLiveDatas";
import { StatusDot } from "./OnlineStatusFilter";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      "& > .card-body": {
        padding: theme.spacing(1.5),
      },
    },
    cardTitle: {
      marginBottom: theme.spacing(0.5),
    },
    // the menu button is taller than the name; it overlaps the space around
    // the name rather than adding to it
    titleMenu: {
      margin: theme.spacing(-1.5, 0),
    },
    status: {
      display: "flex",
      alignItems: "center",
    },
    alertsRoot: {
      position: "relative",
      // as high as a small chip
      height: theme.spacing(3),
      overflow: "hidden",
    },
    alertsInner: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexWrap: "nowrap",
      textTransform: "uppercase",
      "& > *": {
        margin: theme.spacing(0.5),
      },
    },
    gauge: {
      width: 100,
      height: 100,
    },
    table: {
      width: "100%",
      tableLayout: "fixed",
    },
    cellText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cellTwoLines: {
      lineHeight: 1.2,
      height: "2.4em",
      overflow: "hidden",
    },
    cellLine: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  })
);

interface GaugeProps {
  pct: number;
}

const LinearProgressWithLabel = (props: GaugeProps): JSX.Element => {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" value={props.pct} />
      </Box>
      <Box minWidth={35}>
        <Typography variant="body2" color="textSecondary">{`${Math.round(
          props.pct
        )}%`}</Typography>
      </Box>
    </Box>
  );
};

interface LiveParam {
  value: number;
  updatedAt: string;
}

interface Props {
  onDCXDash?: (e: OnDCXDashEvent) => Promise<void> | void;
  onRelease?: () => void;
  // the order's live data, from useLiveDatas; none until it has been read
  data?: LiveData;
  // whether the order's device is online; not shown when unknown
  status?: OnlineStatus;
  title?: string;
  description?: string;
  serialNumber?: string;
}

const MAX_VISIBLE_ALERTS = 3;

interface GaugeData {
  label: string;
  status: string;
  state: string;
  stateTooltip: string;
  pct: number;
}

export const adcToPct = (val: number): number =>
  Math.round((Math.min(5, Math.max(0, val)) / 5.0) * 100);

export function formatGaugeData(
  params?: LiveParamNamed[] | null
): Record<string, GaugeData> {
  const gaugeData: Record<string, GaugeData> = {
    pv: {
      label: "Solar",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    acout: {
      label: "AC Out",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    heatsinktemp: {
      label: "Heat Sink",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    batt: {
      label: "Battery",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid0: {
      label: "Potable 1",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid1: {
      label: "Potable 2",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid2: {
      label: "Grey 1",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid5: {
      label: "Grey 2",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid3: {
      label: "Waste",
      status: "N/A",
      state: "",
      stateTooltip: "",
      pct: 0,
    },
    liquid4: {
      label: "Fuel",
      status: "N/A",
      state: "Unknown",
      stateTooltip: "",
      pct: 0,
    },
  };
  if (params) {
    const byName: Record<string, LiveParam> = {};
    params.forEach((p) => {
      if (p.value !== null && p.updatedAt !== null) {
        byName[p.name] = { value: p.value, updatedAt: p.updatedAt };
      }
    });

    if (byName["pv.power"]) {
      const NOMINAL_ECO_SITE_PV_MAX = 3000; // Watts
      gaugeData["pv"].status = `${byName["pv.power"].value / 1000}kW`;
      gaugeData["pv"].pct =
        (byName["pv.power"].value / NOMINAL_ECO_SITE_PV_MAX) * 100;
    }
    if (byName["inverter.ac_out_power"]) {
      const NOMINAL_ECO_SITE_AC_OUT_MAX = 5000; // Watts
      gaugeData["acout"].status = `${
        byName["inverter.ac_out_power"].value / 1000
      }kW`;
      gaugeData["acout"].pct =
        (byName["inverter.ac_out_power"].value / NOMINAL_ECO_SITE_AC_OUT_MAX) *
        100;
    }
    if (byName["inverter.heat_sink_temp"]) {
      const NOMINAL_ECO_SITE_HEAT_SINK_TEMP_MAX = 100; // Deg C
      gaugeData[
        "heatsinktemp"
      ].status = `${byName["inverter.heat_sink_temp"].value}°C`;
      gaugeData["heatsinktemp"].pct =
        (byName["inverter.heat_sink_temp"].value /
          NOMINAL_ECO_SITE_HEAT_SINK_TEMP_MAX) *
        100;
    }
    if (byName["battery.soc"]) {
      gaugeData["batt"].status = ``;
      gaugeData["batt"].pct = byName["battery.soc"].value;
    }
    if (byName["battery.total_voltage"]) {
      // Shown in brackets after the label, like Solar's kW. The bar stays SOC.
      gaugeData["batt"].status = `${byName[
        "battery.total_voltage"
      ].value.toFixed(1)}V`;
    }

    const liquids = [
      { record: "liquid0", liveParam: byName["liquids.level0"] },
      { record: "liquid1", liveParam: byName["liquids.level1"] },
      { record: "liquid2", liveParam: byName["liquids.level2"] },
      { record: "liquid5", liveParam: byName["liquids.level5"] },
      { record: "liquid3", liveParam: byName["liquids.level3"] },
    ];
    liquids.forEach((l) => {
      if (l.liveParam) {
        gaugeData[l.record].status = ``;
        gaugeData[l.record].pct = adcToPct(l.liveParam.value);
      }
    });

    if (byName["liquids.level4"]) {
      gaugeData["liquid4"].status = ``;
      if (0 === byName["liquids.level4"].value) {
        gaugeData["liquid4"].state = "Unknown";
        gaugeData["liquid4"].stateTooltip = "Not Connected";
      } else {
        gaugeData["liquid4"].state = "";
        gaugeData["liquid4"].stateTooltip = "";
        gaugeData["liquid4"].pct = adcToPct(byName["liquids.level4"].value);
      }
    }
  }
  return gaugeData;
}

// The online status shown on a live card, e.g. "Offline · last report 2 hours
// ago"; none when unknown.
export function liveStatusText(
  status: OnlineStatus | undefined,
  data?: LiveData
): string {
  if ("online" === status) return "Online";
  if ("offline" !== status) return "";
  const lastUpdated = lastUpdatedAt(data);
  return lastUpdated
    ? `Offline · last report ${moment(lastUpdated).fromNow()}`
    : "Offline · never reported";
}

// A live card. The grid it is in reads the live data of all its orders in one
// call (useLiveDatas) and hands each card its own.
const LiveInternal: React.FC<Props> = ({
  onDCXDash,
  onRelease,
  data,
  status,
  title = "DCX",
  description,
  serialNumber,
}) => {
  const { user } = useUser();

  const gaugeData = formatGaugeData(data?.params);

  let alerts = [] as Alert[];
  let visibleAlerts = [] as Alert[];
  let hiddenAlerts = [] as Alert[];
  let updatedAt = "N/A";
  let updatedAtDate = "N/A";
  let updatedAtTime = "";
  const lastUpdated = lastUpdatedAt(data);
  if (lastUpdated) {
    const updated = moment(lastUpdated);
    updatedAt = updated.format("LLL");
    updatedAtDate = updated.format("MMM D, YYYY");
    updatedAtTime = updated.format("LT");
  }
  if (data) {
    alerts = (data.alerts || []).filter(
      (x) => x.level === "warn" || x.level === "error"
    );
    visibleAlerts = alerts.slice(0, MAX_VISIBLE_ALERTS);
    hiddenAlerts = alerts.slice(MAX_VISIBLE_ALERTS);
  }

  const classes = useStyles();

  const guages = ["pv"];
  const allowedACOut = user.features.hasFeature("web.dcx.inverter.acout");
  if (allowedACOut) {
    guages.push("acout");
  }
  const allowedHeatSinkTemp = user.features.hasFeature(
    "web.dcx.inverter.heatsinktemp"
  );
  if (allowedHeatSinkTemp) {
    guages.push("heatsinktemp");
  }
  guages.push(
    "batt",
    "liquid0",
    "liquid1",
    "liquid2",
    "liquid5",
    "liquid3",
    "liquid4"
  );

  return (
    <Card body className={classes.card}>
      <Card.Title className={classes.cardTitle}>
        <div className="d-flex justify-content-between align-items-center">
          <div>{title}</div>
          <div className={classes.titleMenu}>
            {(onDCXDash || onRelease) && (
              <ActionMenu onDCXDash={onDCXDash} onRelease={onRelease} />
            )}
          </div>
        </div>
        {(status === "online" || status === "offline") && (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.status}
            data-testid="live-status"
          >
            <StatusDot status={status} />
            {liveStatusText(status, data)}
          </Typography>
        )}
        <div className={classes.alertsRoot}>
          <div className={classes.alertsInner}>
            {visibleAlerts.map((x, i) => (
              <Tooltip title={x.message} key={`${x.name}-${i}`}>
                <Chip label={x.name} size="small" icon={<ErrorIcon />} />
              </Tooltip>
            ))}
            {hiddenAlerts.length > 0 && (
              <Tooltip title={hiddenAlerts.map((x) => x.name).join(", ")}>
                <Chip label={`+${hiddenAlerts.length} more`} size="small" />
              </Tooltip>
            )}
          </div>
        </div>
      </Card.Title>
      <TableContainer>
        <Table
          className={classes.table}
          aria-label="Live data fields"
          size="small"
          padding="none"
        >
          <colgroup>
            <col style={{ width: "55%" }} />
            <col style={{ width: "45%" }} />
          </colgroup>
          <TableBody>
            {description !== undefined && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Description
                </TableCell>
                <TableCell align="right" className={classes.cellText}>
                  <Tooltip title={description}>
                    <span>{description}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
            {serialNumber !== undefined && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Serial number
                </TableCell>
                <TableCell align="right" className={classes.cellText}>
                  <Tooltip title={serialNumber}>
                    <span>{serialNumber}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell component="th" scope="row">
                Updated
              </TableCell>
              <TableCell align="right" className={classes.cellTwoLines}>
                <Tooltip title={updatedAt}>
                  <span>
                    <span className={classes.cellLine}>{updatedAtDate}</span>
                    {updatedAtTime && (
                      <span className={classes.cellLine}>{updatedAtTime}</span>
                    )}
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
            {guages.map((key, i) => (
              <TableRow key={key}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.cellText}
                >
                  {gaugeData[key].label}
                  {gaugeData[key].status !== "" && `(${gaugeData[key].status})`}
                </TableCell>
                <TableCell align="right">
                  {gaugeData[key].state !== "" && (
                    <span className="pr-1">
                      <Tooltip title={gaugeData[key].stateTooltip}>
                        <InfoOutlined />
                      </Tooltip>
                    </span>
                  )}
                  {gaugeData[key].state !== "" && `${gaugeData[key].state}`}
                  {gaugeData[key].state === "" && (
                    <LinearProgressWithLabel key={i} pct={gaugeData[key].pct} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default withApolloProvider(ConnXClient, LiveInternal);
